import { AxiosResponse } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useLoaderData, useNavigate, useParams } from 'react-router';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import PoTable from './PoTable';
import RHFDatePicker from '@/components/date-picker/RHFDatePicker';
import CalibrationTaskBreadcrumbs from './CalibrationTaskBreadcrumbs';
import { CalibrationTaskFormSchema, calibrationTaskFormSchema } from './zodSchemas';
import { useMutation, useQuery } from '@tanstack/react-query';
import { startCalibrationTask, toCalibrationTaskPo } from './constants';
import { extractErrorData, getUIErrorMessage } from '@/utils/errors';
import { useModal } from '@/components/modal/useModal';
import ConfirmationModal from '@/components/modal/ConfirmationModal';
import { CalibrationOperationType } from './types';
import { ROUTES } from '@/constants';
import { queryProcessObject } from '@/routes/process-objects/list/constants.ts';
import { useGetProcessObjectsByIds } from '../process-objects/list/useGetProcessObjectsByIds';
import { MeasurementGroupDetails } from '../measurement-group/types';
import { ProcessObject } from '../process-objects/list/types';
import useTitle from '@/hooks/useTitle.ts';

const modalDetails: Record<CalibrationOperationType, Record<string, string>> = {
	GET_DATA: {
		title: 'Calibration file download',
		body: 'This action will schedule the execution of a task that will result in hystorical data being available for download.'
	},
	UNIT_VECTOR: {
		title: 'Start unit vector creation',
		body: 'This action will schedule the execution of a new unit vector creation process.'
	},
	CROSS_VALIDATION: {
		title: 'Start cross validation',
		body: 'This action will schedule the execution of a new cross validation process.'
	}
};

const startButtons: { operationType: CalibrationOperationType; label: string }[] = [
	{
		operationType: CalibrationOperationType.GET_DATA,
		label: 'Download calibration file'
	},
	{
		operationType: CalibrationOperationType.CROSS_VALIDATION,
		label: 'Start cross validation'
	},
	{
		operationType: CalibrationOperationType.UNIT_VECTOR,
		label: 'Start unit vector creation'
	}
];

const CalibrationTask = () => {
	const { measurementGroupId } = useParams();
	const navigate = useNavigate();
	const { measurementGroup } = useLoaderData() as {
		measurementGroup: AxiosResponse<MeasurementGroupDetails>;
	};

	useTitle(`Calibrations ${measurementGroup.data.name}`);

	const { paginatedProcessObjects } = useGetProcessObjectsByIds(
		measurementGroup.data.devices.map(device => device.poId.toString()),
		measurementGroup.data.devices.length > 0
	);

	const processObjectsList = paginatedProcessObjects?.data.items ?? [];

	const { isOpen, closeModal, openModal } = useModal();

	const mapToCalibrationTaskPo = useCallback(
		(po: ProcessObject) => {
			return toCalibrationTaskPo(po, parseInt(measurementGroupId as string));
		},
		[measurementGroupId]
	);

	const defaultValues: CalibrationTaskFormSchema = {
		name: measurementGroup.data.name,
		id: measurementGroup.data.id,
		startDate: null,
		endDate: null,
		interceptFlag: false,
		referenceData: new File([], ''),
		taskType: CalibrationOperationType.GET_DATA,
		poList: processObjectsList.map(po => mapToCalibrationTaskPo(po))
	};

	const calibrationTaskForm = useForm<CalibrationTaskFormSchema>({
		resolver: zodResolver(calibrationTaskFormSchema),
		defaultValues
	});

	const { fields, append, remove } = useFieldArray({
		control: calibrationTaskForm.control,
		name: 'poList',
		keyName: '_internalId'
	});

	useEffect(() => {
		if (paginatedProcessObjects?.data.items) {
			calibrationTaskForm.setValue(
				'poList',
				paginatedProcessObjects.data.items.map(po => mapToCalibrationTaskPo(po))
			);
		}
	}, [paginatedProcessObjects?.data.items, calibrationTaskForm, measurementGroupId, mapToCalibrationTaskPo]);

	const [additionalPoId, setAdditionalPoId] = useState('');
	const [additonalPoIdError, setAdditionalPoIdError] = useState('');

	const { refetch: fetchProcessObject, isFetching: isFetchingPo } = useQuery({
		queryKey: ['process-object', additionalPoId],
		queryFn: () => queryProcessObject(additionalPoId),
		enabled: false
	});

	const handleAddPo = async () => {
		const isPoIdValid = validatePoId();

		if (!isPoIdValid) return;

		try {
			const res = await fetchProcessObject();

			if (!res.data) {
				setAdditionalPoIdError('Error fetching PO data.');
				return;
			}
			append(toCalibrationTaskPo(res.data.data, parseInt(measurementGroupId as string)));
			setAdditionalPoId('');
		} catch (e) {
			const errorData = extractErrorData(e);
			setAdditionalPoIdError(errorData.errorMessage);
		}
	};

	const [calibrationTaskError, setCalibrationTaskError] = useState('');
	const { mutate: submitCalibrationTask, isPending: isSubmitting } = useMutation({
		mutationFn: startCalibrationTask,
		mutationKey: ['calibration-task', measurementGroupId],
		onSuccess: () => {
			closeModal();
			navigate(ROUTES.tasks);
		},
		onError: error => {
			setCalibrationTaskError(getUIErrorMessage(error));
		}
	});

	function onSubmit() {
		submitCalibrationTask(calibrationTaskForm.getValues());
	}

	const removePo = (index: number) => {
		remove(index);
	};

	const validatePoId = () => {
		if (!additionalPoId) {
			setAdditionalPoIdError('Process object ID is required');
			return false;
		}

		if (Number(additionalPoId) < 0) {
			setAdditionalPoIdError("Process object ID can't be negative");
			return;
		}

		if (isNaN(Number(additionalPoId))) {
			setAdditionalPoIdError('Process object ID must be a number');
			return false;
		}
		setAdditionalPoIdError('');
		return true;
	};

	const isStartDateDisabled = (date: Date) => {
		const endDate = calibrationTaskForm.getValues('endDate');
		return date > new Date() || date < new Date('1900-01-01') || (!!endDate && date > endDate);
	};

	const isEndDateDisabled = (date: Date) => {
		const startDate = calibrationTaskForm.getValues('startDate');
		return date > new Date() || date < new Date('1900-01-01') || (!!startDate && date < startDate);
	};

	const handleCloseModal = () => {
		closeModal();
		setCalibrationTaskError('');
	};

	const handleFileValidationAndOpenModal = (operationType: CalibrationOperationType) => {
		calibrationTaskForm.setValue('taskType', operationType);

		const { referenceData } = calibrationTaskForm.getValues();
		const isFileRequired = operationType === CalibrationOperationType.CROSS_VALIDATION;

		if (isFileRequired && (!referenceData || referenceData.size === 0)) {
			calibrationTaskForm.setError('referenceData', {
				message: 'Reference data is required for this operation.'
			});
			return;
		}

		openModal();
	};

	const taskType = calibrationTaskForm.getValues('taskType');

	const handleClickStartBtn = (operationType: CalibrationOperationType) => {
		const submitHandler = calibrationTaskForm.handleSubmit(() => handleFileValidationAndOpenModal(operationType));
		submitHandler();
	};

	return (
		<div className="flex flex-col gap-10">
			<CalibrationTaskBreadcrumbs measurementGroupId={measurementGroupId} />

			<Form {...calibrationTaskForm}>
				<form className="w-full flex flex-col gap-10">
					<div className="w-full flex lg:flex-row gap-10 flex-col">
						<div className="space-y-4 w-full lg:w-2/6 max-w-sm">
							<FormField
								control={calibrationTaskForm.control}
								name="name"
								disabled
								render={({ field }) => (
									<FormItem>
										<FormLabel>Measurement group name: </FormLabel>
										<div className=" truncate text-xs" title={field.value}>
											{field.value}
										</div>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={calibrationTaskForm.control}
								name="id"
								disabled
								render={({ field }) => (
									<FormItem>
										<FormLabel>Measurement group id: </FormLabel>
										<div className="text-xs">{field.value}</div>
										<FormMessage />
									</FormItem>
								)}
							/>
							<RHFDatePicker
								control={calibrationTaskForm.control}
								name="startDate"
								label="Start date"
								placeholder="Pick a start date"
								isDisabledFn={isStartDateDisabled}
							/>
							<RHFDatePicker
								control={calibrationTaskForm.control}
								name="endDate"
								label="End date"
								placeholder="Pick an end date"
								isDisabledFn={isEndDateDisabled}
							/>

							<FormField
								control={calibrationTaskForm.control}
								name="interceptFlag"
								disabled
								render={({ field }) => (
									<FormItem className="flex flex-row items-start space-x-3 space-y-0">
										<FormControl>
											<Checkbox
												checked={field.value}
												onCheckedChange={() => field.onChange(!field.value)}
											/>
										</FormControl>
										<div className="space-y-1 leading-none">
											<FormLabel>Intercept flag</FormLabel>
										</div>
									</FormItem>
								)}
							/>

							<FormField
								control={calibrationTaskForm.control}
								name="referenceData"
								render={({ field }) => (
									<FormItem className="w-[240px]">
										<FormLabel>Reference data</FormLabel>
										<FormControl>
											<Input
												type="file"
												onChange={e => field.onChange(e.target.files?.[0])}
												onBlur={field.onBlur}
												name={field.name}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="w-full lg:w-4/6 flex flex-col gap-5">
							<div className="border rounded-md">
								<PoTable
									items={fields}
									control={calibrationTaskForm.control}
									removePo={removePo}
									isLoading={isFetchingPo}
								/>
							</div>
							<div className="flex justify-end gap-5">
								<FormItem className="flex flex-col">
									<FormControl>
										<Input
											placeholder="Process object ID"
											onChange={e => setAdditionalPoId(e.target.value)}
											onKeyDown={e => {
												if (e.key === 'Enter') {
													e.preventDefault();
													handleAddPo();
												}
											}}
											value={additionalPoId}
											className="w-64"
											disabled={isFetchingPo}
										/>
									</FormControl>
									<p className="text-[0.8rem] font-medium text-destructive">{additonalPoIdError}</p>
								</FormItem>

								<Button variant="outline" onClick={handleAddPo} type="button" disabled={isFetchingPo}>
									Add PO
								</Button>
							</div>
						</div>
					</div>

					<div className="flex flex-col gap-2 lg:flex-row lg:justify-start lg:gap-5">
						{startButtons.map(({ operationType, label }) => (
							<Button
								type="button"
								key={operationType}
								onClick={() => handleClickStartBtn(CalibrationOperationType[operationType])}
							>
								{label}
							</Button>
						))}
					</div>
				</form>
			</Form>
			<ConfirmationModal
				isOpen={isOpen}
				handleClose={handleCloseModal}
				title={modalDetails[taskType].title}
				body={modalDetails[taskType].body}
				handleConfirm={onSubmit}
				isDisabled={isSubmitting}
				errorMessage={calibrationTaskError}
				isLoading={isSubmitting}
				confirmBtnLabel="Submit"
			/>
		</div>
	);
};

export default CalibrationTask;
